/**
 * Fine Art Ontology - Aligned with Scraper data model
 * See: Scraper/docs/plans/2026-02-09-fine-art-ontology-design.md
 */

// =============================================================================
// PERIODS (Primary Navigation)
// =============================================================================

export const PERIODS = [
  { id: 'old-masters', name: 'Old Masters', dateRange: 'pre-1800', description: 'Renaissance through Rococo' },
  { id: '19th-century', name: '19th Century', dateRange: '1800-1880', description: 'Neoclassicism, Romanticism, Realism' },
  { id: 'modern', name: 'Modern', dateRange: '1880-1945', description: 'Impressionism through WWII' },
  { id: 'post-war', name: 'Post-War', dateRange: '1945-1970', description: 'Abstract Expressionism through Minimalism' },
  { id: 'contemporary', name: 'Contemporary', dateRange: '1970-now', description: 'Conceptual, Neo-Expressionism, Digital' },
] as const

export type PeriodId = typeof PERIODS[number]['id']

// =============================================================================
// MEDIUMS (Secondary Filter - 6 canonical categories)
// =============================================================================

export const MEDIUMS = [
  { id: 'paintings', name: 'Paintings', barnebyId: 1, description: 'Oil, acrylic, tempera, encaustic' },
  { id: 'drawings', name: 'Drawings', barnebyId: 5, description: 'Pencil, charcoal, pastel, watercolor' },
  { id: 'prints', name: 'Prints', barnebyId: 2, description: 'Lithographs, etchings, screenprints, woodcuts' },
  { id: 'photography', name: 'Photography', barnebyId: 3, description: 'All photographic processes' },
  { id: 'sculpture', name: 'Sculpture', barnebyId: 4, description: 'Bronze, marble, wood, found objects, installations' },
  { id: 'mixed-media', name: 'Mixed Media', barnebyId: 6, description: 'Collage, assemblage, multimedia' },
] as const

export type MediumId = typeof MEDIUMS[number]['id']

// =============================================================================
// MOVEMENTS (45+ art movements by era)
// =============================================================================

export interface Movement {
  id: string
  name: string
  period: PeriodId
  startYear?: number
  endYear?: number
  keywords?: string[]
}

// Pre-Modern Movements (Old Masters + 19th Century)
export const PRE_MODERN_MOVEMENTS: Movement[] = [
  { id: 'renaissance', name: 'Renaissance', period: 'old-masters', startYear: 1400, endYear: 1600 },
  { id: 'baroque', name: 'Baroque', period: 'old-masters', startYear: 1600, endYear: 1750 },
  { id: 'rococo', name: 'Rococo', period: 'old-masters', startYear: 1700, endYear: 1800 },
  { id: 'neoclassicism', name: 'Neoclassicism', period: '19th-century', startYear: 1760, endYear: 1850 },
  { id: 'romanticism', name: 'Romanticism', period: '19th-century', startYear: 1790, endYear: 1880 },
  { id: 'realism', name: 'Realism', period: '19th-century', startYear: 1840, endYear: 1880 },
  { id: 'pre-raphaelite', name: 'Pre-Raphaelite', period: '19th-century', startYear: 1848, endYear: 1880 },
  { id: 'hudson-river-school', name: 'Hudson River School', period: '19th-century', startYear: 1825, endYear: 1875 },
]

// Modern Movements (1880-1945)
export const MODERN_MOVEMENTS: Movement[] = [
  { id: 'impressionism', name: 'Impressionism', period: 'modern', startYear: 1860, endYear: 1900 },
  { id: 'post-impressionism', name: 'Post-Impressionism', period: 'modern', startYear: 1886, endYear: 1910 },
  { id: 'fauvism', name: 'Fauvism', period: 'modern', startYear: 1904, endYear: 1908 },
  { id: 'expressionism', name: 'Expressionism', period: 'modern', startYear: 1905, endYear: 1930 },
  { id: 'cubism', name: 'Cubism', period: 'modern', startYear: 1907, endYear: 1920 },
  { id: 'futurism', name: 'Futurism', period: 'modern', startYear: 1909, endYear: 1920 },
  { id: 'dada', name: 'Dada', period: 'modern', startYear: 1916, endYear: 1924 },
  { id: 'surrealism', name: 'Surrealism', period: 'modern', startYear: 1924, endYear: 1945 },
  { id: 'bauhaus', name: 'Bauhaus', period: 'modern', startYear: 1919, endYear: 1933 },
  { id: 'de-stijl', name: 'De Stijl', period: 'modern', startYear: 1917, endYear: 1931 },
  { id: 'constructivism', name: 'Constructivism', period: 'modern', startYear: 1915, endYear: 1940 },
  { id: 'mexican-muralism', name: 'Mexican Muralism', period: 'modern', startYear: 1920, endYear: 1960 },
  { id: 'harlem-renaissance', name: 'Harlem Renaissance', period: 'modern', startYear: 1920, endYear: 1940 },
]

// Post-War Movements (1945-1970)
export const POST_WAR_MOVEMENTS: Movement[] = [
  { id: 'abstract-expressionism', name: 'Abstract Expressionism', period: 'post-war', startYear: 1943, endYear: 1965 },
  { id: 'pop-art', name: 'Pop Art', period: 'post-war', startYear: 1955, endYear: 1970 },
  { id: 'minimalism', name: 'Minimalism', period: 'post-war', startYear: 1960, endYear: 1975 },
  { id: 'conceptual-art', name: 'Conceptual Art', period: 'post-war', startYear: 1965, endYear: 1975 },
  { id: 'fluxus', name: 'Fluxus', period: 'post-war', startYear: 1961, endYear: 1978 },
  { id: 'arte-povera', name: 'Arte Povera', period: 'post-war', startYear: 1967, endYear: 1972 },
  { id: 'op-art', name: 'Op Art', period: 'post-war', startYear: 1960, endYear: 1970 },
  { id: 'color-field', name: 'Color Field', period: 'post-war', startYear: 1955, endYear: 1970 },
  { id: 'photorealism', name: 'Photorealism', period: 'post-war', startYear: 1965, endYear: 1980 },
  { id: 'land-art', name: 'Land Art', period: 'post-war', startYear: 1967, endYear: 1977 },
  { id: 'gutai', name: 'Gutai', period: 'post-war', startYear: 1954, endYear: 1972 },
  { id: 'nouveau-realisme', name: 'Nouveau Réalisme', period: 'post-war', startYear: 1960, endYear: 1970 },
]

// Contemporary Movements (1970-now)
export const CONTEMPORARY_MOVEMENTS: Movement[] = [
  { id: 'neo-expressionism', name: 'Neo-Expressionism', period: 'contemporary', startYear: 1978, endYear: 1990 },
  { id: 'street-art', name: 'Street Art', period: 'contemporary', startYear: 1980 },
  { id: 'yba', name: 'YBA (Young British Artists)', period: 'contemporary', startYear: 1988, endYear: 2000 },
  { id: 'neo-pop', name: 'Neo-Pop', period: 'contemporary', startYear: 1980 },
  { id: 'relational-aesthetics', name: 'Relational Aesthetics', period: 'contemporary', startYear: 1990 },
  { id: 'digital-art', name: 'Digital Art', period: 'contemporary', startYear: 1990 },
  { id: 'afrofuturism', name: 'Afrofuturism', period: 'contemporary', startYear: 1990 },
  { id: 'superflat', name: 'Superflat', period: 'contemporary', startYear: 2000 },
  { id: 'african-contemporary', name: 'African Contemporary', period: 'contemporary', startYear: 2000 },
  { id: 'chinese-contemporary', name: 'Chinese Contemporary', period: 'contemporary', startYear: 1990 },
  { id: 'figurative-revival', name: 'Figurative Revival', period: 'contemporary', startYear: 2010 },
  { id: 'post-internet', name: 'Post-Internet', period: 'contemporary', startYear: 2008 },
  { id: 'nft-art', name: 'NFT/Crypto Art', period: 'contemporary', startYear: 2017 },
]

export const ALL_MOVEMENTS: Movement[] = [
  ...PRE_MODERN_MOVEMENTS,
  ...MODERN_MOVEMENTS,
  ...POST_WAR_MOVEMENTS,
  ...CONTEMPORARY_MOVEMENTS,
]

export type MovementId = typeof ALL_MOVEMENTS[number]['id']

// =============================================================================
// ARTIST TIERS
// =============================================================================

export const ARTIST_TIERS = [
  {
    id: 'blue-chip',
    name: 'Blue-chip',
    description: 'Record >$10M OR avg hammer >$1M OR 50+ lots/year at majors',
    color: 'accent-primary',
  },
  {
    id: 'mid-market',
    name: 'Mid-market',
    description: 'Avg hammer $50K-$1M, consistent presence',
    color: 'accent-warm',
  },
  {
    id: 'emerging',
    name: 'Emerging',
    description: '<5 years history OR avg <$50K, upward trajectory',
    color: 'positive',
  },
  {
    id: 'unclassified',
    name: 'Unclassified',
    description: 'Insufficient data',
    color: 'text-tertiary',
  },
] as const

export type TierId = typeof ARTIST_TIERS[number]['id']

// =============================================================================
// CAREER STAGES
// =============================================================================

export const CAREER_STAGES = [
  { id: 'emerging', name: 'Emerging', description: 'Born after 1980 AND <10 years since first major sale' },
  { id: 'mid-career', name: 'Mid-career', description: '10-25 years active, still producing' },
  { id: 'established', name: 'Established', description: '25+ years OR major museum retrospective' },
  { id: 'historical', name: 'Historical', description: 'Deceased OR born before 1940' },
] as const

export type CareerStageId = typeof CAREER_STAGES[number]['id']

// =============================================================================
// REGIONS (for geographic filtering)
// =============================================================================

export const REGIONS = [
  { id: 'global', name: 'Global' },
  { id: 'americas', name: 'Americas' },
  { id: 'europe', name: 'Europe' },
  { id: 'asia-pacific', name: 'Asia-Pacific' },
  { id: 'middle-east', name: 'Middle East' },
] as const

export type RegionId = typeof REGIONS[number]['id']

// =============================================================================
// TIME RANGES
// =============================================================================

export const TIME_RANGES = [
  { id: '30d', name: '30 days', days: 30 },
  { id: '12m', name: '12 months', days: 365 },
  { id: 'ytd', name: 'YTD', days: null }, // calculated dynamically
  { id: 'all', name: 'All time', days: null },
] as const

export type TimeRangeId = typeof TIME_RANGES[number]['id']

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getPeriodById(id: PeriodId) {
  return PERIODS.find(p => p.id === id)
}

export function getMediumById(id: MediumId) {
  return MEDIUMS.find(m => m.id === id)
}

export function getMovementById(id: string) {
  return ALL_MOVEMENTS.find(m => m.id === id)
}

export function getMovementsByPeriod(periodId: PeriodId) {
  return ALL_MOVEMENTS.filter(m => m.period === periodId)
}

export function getTrendingMovements(): Movement[] {
  // These would come from the API in production
  return [
    getMovementById('street-art')!,
    getMovementById('african-contemporary')!,
    getMovementById('superflat')!,
    getMovementById('figurative-revival')!,
    getMovementById('neo-expressionism')!,
    getMovementById('chinese-contemporary')!,
    getMovementById('pop-art')!,
    getMovementById('photorealism')!,
  ]
}
